import {
  Button,
  Card,
  CardContent,
  H5,
  InputDate,
  InputNote,
  InputText,
  Switch,
} from "@sellar-io/components";
import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import { useState } from "react";
import { useForm, useSelector } from "@tanstack/react-form";
import { Alert } from "../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components/radioSelector/RadioGroup";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useSupplierOutOfOfficeCreate } from "../../../hooks/api/v2/supplierOutOfOffice/useSupplierOutOfOfficeCreate";
import { useSupplierOutOfOfficeUpdate } from "../../../hooks/api/v2/supplierOutOfOffice/useSupplierOutOfOfficeUpdate";

export const StorefrontSettingSupplierOutOfOffice = ({
  supplier,
}: {
  supplier: SupplierGetResponseType;
}) => {
  return (
    <>
      {supplier.SupplierOutOfOffice?.id ? (
        <StorefrontSettingSupplierOutOfOfficeUpdate
          supplier={supplier}
          supplierOutOfOfficeId={supplier.SupplierOutOfOffice.id}
        />
      ) : (
        <StorefrontSettingSupplierOutOfOfficeCreate supplier={supplier} />
      )}
    </>
  );
};

const StorefrontSettingSupplierOutOfOfficeCreate = ({
  supplier,
}: {
  supplier: SupplierGetResponseType;
}) => {
  const createSupplierOutOfOfficeMutation = useSupplierOutOfOfficeCreate(
    supplier.id,
  );

  return (
    <>
      {createSupplierOutOfOfficeMutation.isError && (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(createSupplierOutOfOfficeMutation.error)?.response
              ?.data.message ?? "Unknown error"
          }
          className={"w-full"}
        />
      )}
      <StorefrontSettingSupplierOutOfOfficeCard
        supplier={supplier}
        loading={createSupplierOutOfOfficeMutation.isPending}
        onSubmit={(data) =>
          createSupplierOutOfOfficeMutation.mutateAsync({
            outOfOffice: data.outOfOffice,
            automaticallySwitchOff: data.automaticallySwitchOff,
            message: data.message,
            returnDate: new Date(data.returnDate),
          })
        }
      />
    </>
  );
};

const StorefrontSettingSupplierOutOfOfficeUpdate = ({
  supplier,
  supplierOutOfOfficeId,
}: {
  supplier: SupplierGetResponseType;
  supplierOutOfOfficeId: number;
}) => {
  const updateSupplierOutOfOfficeMutation = useSupplierOutOfOfficeUpdate(
    supplier.id,
    supplierOutOfOfficeId,
  );

  return (
    <>
      {updateSupplierOutOfOfficeMutation.isError && (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(updateSupplierOutOfOfficeMutation.error)?.response
              ?.data.message ?? "Unknown error"
          }
          className={"w-full"}
        />
      )}
      <StorefrontSettingSupplierOutOfOfficeCard
        supplier={supplier}
        loading={updateSupplierOutOfOfficeMutation.isPending}
        onSubmit={(data) =>
          updateSupplierOutOfOfficeMutation.mutateAsync({
            outOfOffice: data.outOfOffice,
            automaticallySwitchOff: data.automaticallySwitchOff,
            message: data.message,
            returnDate: new Date(data.returnDate),
          })
        }
      />
    </>
  );
};

type OutOfOfficeFormValues = {
  outOfOffice: boolean;
  returnDate: string;
  automaticallySwitchOff: boolean;
  message: string;
};

const getFormValues = (
  supplier: SupplierGetResponseType,
): OutOfOfficeFormValues => ({
  outOfOffice: !!supplier.SupplierOutOfOffice?.outOfOffice,
  returnDate: dayjs(supplier.SupplierOutOfOffice?.returnDate).format(
    "YYYY-MM-DD",
  ),
  automaticallySwitchOff:
    !!supplier.SupplierOutOfOffice?.automaticallySwitchOff,
  message: supplier.SupplierOutOfOffice?.message ?? "",
});

const validateForm = ({ value }: { value: OutOfOfficeFormValues }) => {
  const errors: { message?: string; returnDate?: string } = {};

  if (!value.message) {
    errors.message = "Need to define message";
  }

  if (!value.returnDate) {
    errors.returnDate =
      "You need to set a Return date to turn on Out of off mode";
  } else if (
    value.outOfOffice &&
    dayjs(value.returnDate).isBefore(dayjs(), "day")
  ) {
    errors.returnDate = "Return date must be today or in the future";
  }

  if (!errors.message && !errors.returnDate) return undefined;

  return { fields: errors };
};

const StorefrontSettingSupplierOutOfOfficeCard = ({
  supplier,
  onSubmit,
  loading,
}: {
  supplier: SupplierGetResponseType;
  onSubmit: (data: OutOfOfficeFormValues) => Promise<unknown>;
  loading: boolean;
}) => {
  const [defaultValues, setDefaultValues] = useState(() =>
    getFormValues(supplier),
  );

  const form = useForm({
    defaultValues,
    validators: {
      onChange: validateForm,
      onSubmit: validateForm,
    },
    onSubmit: async ({ value }) => {
      try {
        await onSubmit(value);
        form.reset(value);
        setDefaultValues(value);
      } catch {
        // Mutation error is surfaced separately via the parent's isError
        // Alert; leave the form dirty so the user doesn't lose unsaved changes.
      }
    },
  });

  const { outOfOffice, returnDate, isDirty } = useSelector(
    form.store,
    (state) => ({
      outOfOffice: state.values.outOfOffice,
      returnDate: state.values.returnDate,
      isDirty: state.isDirty,
    }),
  );

  const OOOModeOnTitle = () => {
    const title = "Out of office mode: ON";

    if (!!returnDate) {
      return `${title} until ${dayjs(returnDate).format("DD/MM/YYYY")}`;
    }

    return title;
  };

  return (
    <Card className="bg-white">
      <CardContent className={"p-4"}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
          className={"flex flex-col gap-4"}
        >
          <div className={"flex justify-between"}>
            <InputLabel>Out of office</InputLabel>
            <form.Field name="outOfOffice">
              {(field) => (
                <Switch
                  checked={field.state.value}
                  onChange={(checked) => field.handleChange(checked)}
                />
              )}
            </form.Field>
          </div>
          <div className="">
            <InputLabel>Return date</InputLabel>
            <form.Field name="returnDate">
              {(field) => (
                <>
                  <InputContainer>
                    <InputDate
                      className={"w-full"}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      min={outOfOffice ? dayjs().format("YYYY-MM-DD") : ""}
                    />
                  </InputContainer>
                  {field.state.meta.errors[0] && (
                    <InputNote className="text-red-600">
                      {field.state.meta.errors[0]}
                    </InputNote>
                  )}
                </>
              )}
            </form.Field>
            <InputNote>
              Delivery estimates for any orders placed while you are out of
              office will be calculated from your return date.
            </InputNote>
          </div>
          <div className="">
            <InputLabel className={"pb-2"}>
              Automatically turn off Out of Office on my return date
            </InputLabel>
            <form.Field name="automaticallySwitchOff">
              {(field) => (
                <RadioGroup
                  value={field.state.value ? "yes" : "no"}
                  onChange={(value) => field.handleChange(value === "yes")}
                >
                  <RadioGroupContainer>
                    <RadioGroupOption value={"yes"}>
                      <H5>Yes, turn off Out of Office on my return date</H5>
                    </RadioGroupOption>
                    <RadioGroupOption value={"no"}>
                      <H5>
                        No, I will manually turn Out of Office off when I'm back
                      </H5>
                    </RadioGroupOption>
                  </RadioGroupContainer>
                </RadioGroup>
              )}
            </form.Field>
          </div>
          <div className="">
            <InputLabel>Message to customers</InputLabel>
            <form.Field name="message">
              {(field) => (
                <>
                  <InputContainer>
                    <InputText
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                  </InputContainer>
                  {field.state.meta.errors[0] && (
                    <InputNote className="text-red-600">
                      {field.state.meta.errors[0]}
                    </InputNote>
                  )}
                </>
              )}
            </form.Field>
          </div>

          {supplier.SupplierOutOfOffice?.outOfOffice || !!outOfOffice ? (
            <Alert
              className={"w-full"}
              type={"information"}
              title={OOOModeOnTitle()}
              message={""}
            />
          ) : (
            <Alert className={"w-full"} message={"Out of office mode: OFF"} />
          )}
          <div className="flex justify-end">
            <Button
              className="flex justify-end"
              type={"submit"}
              loading={loading}
              disabled={!isDirty}
            >
              Save changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
